import { Link } from "react-router-dom";
import FlowerButton from "@/components/FlowerButton";
import { CartBasket } from "@/components/CartBasket";
import { useCart } from "@/context/CartContext";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, Minus } from "lucide-react";
import { fmtPrice } from "@/data/mellow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Cart() {
  const { detailed, total, count, setQty, remove, clear } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!detailed.length) return;
    setLoading(true);
    try {
      const items = detailed.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity }));
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Impossible de démarrer le paiement. Réessaie ✿");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
      <p className="font-cursive text-2xl text-[#78353A]/70">Ton petit panier</p>
      <h1 className="font-cursive text-6xl md:text-7xl text-[#78353A] leading-none mb-8">Panier ({count})</h1>

      {!detailed.length ? (
        <div className="notebook p-10 text-center">
          <p className="font-cursive text-3xl text-[#4A3B32] mb-4">Ton panier est tout vide...</p>
          <Link to="/boutique"><FlowerButton>Voir la boutique</FlowerButton></Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4 min-w-0" data-testid="cart-items">
            <div className="rounded-3xl border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] p-4 sm:p-6">
              <p className="font-cursive text-2xl text-[#78353A]/80 text-center mb-2">Dans ton panier ✿</p>
              <CartBasket items={detailed} />
            </div>
            {detailed.map((i) => (
              <div key={i.lookup_key} className="flex flex-wrap sm:flex-nowrap gap-4 items-center p-4 rounded-3xl border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6]" data-testid={`cart-item-${i.lookup_key}`}>
                <Link to={`/produit/${i.lookup_key}`} className="shrink-0"><img src={i.product.img} alt={i.product.name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover object-top" /></Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/produit/${i.lookup_key}`} className="hover:underline decoration-dotted underline-offset-4"><h3 className="font-cursive text-2xl text-[#78353A] leading-tight line-clamp-2">{i.product.name}</h3></Link>
                  <p className="text-sm text-[#4A3B32]/70">{fmtPrice(i.product.price)} / unité</p>
                </div>
                <div className="basis-full sm:basis-auto flex items-center justify-between sm:justify-end gap-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(i.lookup_key, i.quantity - 1)} className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EAECE1]" data-testid={`qty-minus-${i.lookup_key}`} aria-label="Moins"><Minus className="w-4 h-4" /></button>
                    <span className="font-cursive text-2xl w-6 text-center">{i.quantity}</span>
                    <button onClick={() => setQty(i.lookup_key, i.quantity + 1)} className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EAECE1]" data-testid={`qty-plus-${i.lookup_key}`} aria-label="Plus"><Plus className="w-4 h-4" /></button>
                  </div>
                  <div className="font-cursive text-2xl text-[#4A3B32] w-16 text-right">{fmtPrice(i.subtotal)}</div>
                  <button onClick={() => remove(i.lookup_key)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#FADADD]" data-testid={`remove-${i.lookup_key}`} aria-label="Retirer"><Trash2 className="w-4 h-4 text-[#78353A]" /></button>
                </div>
              </div>
            ))}
            <button onClick={clear} className="text-sm text-[#78353A]/70 hover:text-[#78353A] underline decoration-dashed underline-offset-4">Vider le panier</button>
          </div>

          <aside className="lg:col-span-1 min-w-0">
            <div className="sticky top-24 rounded-3xl border-2 border-dashed border-[#D1D5CB] bg-[#EAECE1] p-6">
              <h2 className="font-cursive text-4xl text-[#78353A] leading-none mb-4">Récapitulatif</h2>
              <div className="flex justify-between text-[#4A3B32]/90 mb-2"><span>Sous-total</span><span>{fmtPrice(total)}</span></div>
              <p className="text-xs text-[#4A3B32]/60 mb-4">Livraison et taxes calculées à l'étape suivante ✿</p>
              <p className="text-xs text-[#78353A]/80 mb-4 rounded-2xl bg-[#FAF9F6] border border-dashed border-[#D1D5CB] px-3 py-2" data-testid="cart-promo-hint">Un code promo ? Tu pourras le saisir sur la page de paiement.</p>
              <div className="flex justify-between font-cursive text-3xl text-[#78353A] mb-6"><span>Total</span><span>{fmtPrice(total)}</span></div>
              <FlowerButton onClick={checkout} disabled={loading} data-testid="checkout-btn" className="w-full">
                {loading ? "Un instant..." : "Passer commande"}
              </FlowerButton>
              <p className="mt-3 text-xs text-[#4A3B32]/60 text-center">Paiement sécurisé Stripe</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
